import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { courseService } from '@/services/courseService'
import type { CourseFilters } from '@/types/course'
import type { CreateCourseRequest, UpdateCourseRequest } from '@/types/api'

export function useCourses(filter?: CourseFilters, enabled: boolean = true) {
  return useQuery({
    queryKey: ['courses', filter],
    queryFn: async () => {
      const response = await courseService.getCourses(filter)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch courses')
      }
      return response.data
    },
    enabled,
  })
}

export function useCourse(id: string) {
  return useQuery({
    queryKey: ['courses', id],
    queryFn: async () => {
      const response = await courseService.getCourseById(id)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch course')
      }
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCourseRequest) => courseService.createCourse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] })
    },
  })
}

export function useUpdateCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCourseRequest }) =>
      courseService.updateCourse(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses'] })
      queryClient.invalidateQueries({ queryKey: ['courses', variables.id] })
    },
  })
}

export function useDeleteCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => courseService.deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] })
    },
  })
}

export function usePublishCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => courseService.publishCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] })
    },
  })
}

export function useUnpublishCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => courseService.unpublishCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] })
    },
  })
}
